import { RequiredEnvParams } from '@zorro/environment';
import { IncomingHttpHeaders } from 'http';
import { v4 as uuidv4 } from 'uuid';

import { ApiClient } from '../__generated__';

export const API_KEY_HEADER = 'x-api-key';
export const ALLOW_NO_AUTH_REQUEST = 'allowNoAuthRequest';
export const CORRELATION_ID_HEADER = 'x-zorro-correlation-id';
export const FLOW_ID_HEADER = 'x-zorro-flow-id';
export const IMPERSONATION_HEADER = 'x-zorro-is-impersonated';

type NextSession = {
  accessToken?: string;
};

type Options = {
  accessToken?: string;
  correlationId?: string;
  flowId?: string;
  isImpersonated?: boolean;
};

type ClientOptions = {
  requestHeaders: IncomingHttpHeaders;
};

function nextOptions(
  requestHeaders: IncomingHttpHeaders,
  accessToken?: string,
  correlationId?: string,
  flowId?: string
): Options & ClientOptions {
  return {
    accessToken,
    correlationId: correlationId ?? `${uuidv4()}`,
    flowId,
    // monolithClientHandler derives this from the calling page and overwrites
    // whatever the browser sent. Other callers pass request headers through
    // unchanged, so this is only trustworthy on the monolith-client route.
    isImpersonated: requestHeaders[IMPERSONATION_HEADER] === 'true',
    requestHeaders,
  };
}

function nestOptions(
  accessToken?: string,
  correlationId?: string,
  flowId?: string,
  isImpersonated?: boolean
): Options {
  return {
    accessToken: accessToken?.replace(/^\s*Bearer /u, '') ?? '',
    correlationId,
    flowId,
    isImpersonated,
  };
}

function isClientOptions(obj: unknown): obj is ClientOptions {
  return typeof obj === 'object' && obj !== null && 'requestHeaders' in obj;
}

export const MonolithClientFactory = {
  getInstance(options: Options) {
    const { accessToken, correlationId, flowId, isImpersonated } = options;
    const headers: Record<string, string> = {
      [API_KEY_HEADER]: process.env[RequiredEnvParams.MONOLITH_API_KEY]!,
    };

    if (correlationId) {
      headers[CORRELATION_ID_HEADER] = correlationId;
    }

    if (flowId) {
      headers[FLOW_ID_HEADER] = flowId;
    }
    if (isImpersonated) {
      headers[IMPERSONATION_HEADER] = 'true';
    }

    const referer =
      isClientOptions(options) && options.requestHeaders['referer'];

    if (referer) {
      headers['X-Forwarded-Referer'] = referer;
    }

    // Forward IP-related headers to preserve user's IP address
    const forwardedFor =
      isClientOptions(options) && options.requestHeaders['x-forwarded-for'];
    if (forwardedFor) {
      headers['x-forwarded-for'] = Array.isArray(forwardedFor)
        ? forwardedFor.join(', ')
        : forwardedFor;
    }

    const realIp =
      isClientOptions(options) && options.requestHeaders['x-real-ip'];
    if (realIp) {
      headers['x-real-ip'] = Array.isArray(realIp) ? realIp[0] : realIp;
    }

    if (!process.env[RequiredEnvParams.MONOLITH_URL]) {
      throw new Error('MONOLITH_URL not set');
    }

    // Can't use config service here and port is not parsed out of the box
    const urlBase = process.env[RequiredEnvParams.MONOLITH_URL]!.replace(
      '${MONOLITH_PORT}',
      process.env[RequiredEnvParams.MONOLITH_PORT]!
    );

    return new ApiClient({
      BASE: urlBase,
      TOKEN: accessToken,
      HEADERS: headers,
    });
  },

  getNestInstance(
    accessToken?: string,
    correlationId?: string,
    flowId?: string,
    isImpersonated?: boolean
  ) {
    return this.getInstance(
      nestOptions(accessToken, correlationId, flowId, isImpersonated)
    );
  },

  getNextInstance(
    headers: IncomingHttpHeaders,
    session?: NextSession,
    correlationId?: string,
    flowId?: string
  ) {
    return this.getInstance(
      nextOptions(headers, session?.accessToken, correlationId, flowId)
    ).monolith;
  },
};
